import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../../utils/cn';

interface ModalShellProps {
  /** id nagłówka dialogu (aria-labelledby). */
  labelledBy: string;
  /** Zamknięcie klawiszem Esc. Pomiń, gdy Esc mógłby zgubić niezapisane zmiany (edytor). */
  onEscape?: () => void;
  /** Warstwa z drabiny z-index (tailwind.config.js → zIndex). */
  layer?: 'modal' | 'modal-top';
  /** Klasy panelu (szerokość, wysokość, padding). */
  panelClassName?: string;
  children: React.ReactNode;
}

/**
 * Wspólna rama modali legacy UI.
 *
 * Portal do <body> — ZAWSZE: przodek z `backdrop-filter`/`transform`/`filter` staje się
 * ramką odniesienia dla `position: fixed` (modal był przycinany do nagłówka), a przodek
 * z własnym kontekstem warstw (`relative z-10`) zamykał z-index modala pod plakietką konta.
 * Esc zamyka (opcjonalnie), fokus wchodzi do dialogu i wraca na element, który go otworzył.
 */
export const ModalShell: React.FC<ModalShellProps> = ({
  labelledBy,
  onEscape,
  layer = 'modal',
  panelClassName,
  children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const escRef = useRef(onEscape);
  escRef.current = onEscape;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    panelRef.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && escRef.current) escRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    };
  }, []);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      className={cn(
        'glass-scrim fixed inset-0 flex items-center justify-center p-3 sm:p-5 animate-fadeIn',
        layer === 'modal-top' ? 'z-modal-top' : 'z-modal'
      )}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={cn(
          'glass-float w-full rounded-2xl text-txt-primary flex flex-col overflow-hidden focus:outline-none',
          panelClassName
        )}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};
